import React, { useState, useEffect, useCallback } from "react";
import api from "../../services/api";

function formatDatetime(d) {
  if (!d) return "—";
  return new Date(d).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function timeAgo(d) {
  if (!d) return "—";
  const diff = Date.now() - new Date(d).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function SessionsTab({ currentUser }) {
  const isAdmin = currentUser?.role === "admin";
  const [sessions, setSessions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState("all");
  const [revokeConfirm, setRevokeConfirm] = useState(null);
  const LIMIT = 30;

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit: LIMIT };
      const res = await api.adminGetSessions(params);
      setSessions(res.data.sessions);
      setTotal(res.data.total);
    } catch (e) {
      setError(e.response?.data?.error || "Failed to load sessions");
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRevoke = async (session) => {
    try {
      await api.adminRevokeSession(session.sessionId);
      setRevokeConfirm(null);
      load();
    } catch (e) {
      setError(e.response?.data?.error || "Failed to revoke session");
    }
  };

  const filtered =
    filter === "all"
      ? sessions
      : filter === "active"
        ? sessions.filter((s) => s.active)
        : sessions.filter((s) => !s.active);

  const userName = (session) =>
    session.user?.fullName
      ? `${session.user.fullName} (${session.user?.username})`
      : session.user?.username || session.username;

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
          flexWrap: "wrap",
          gap: "10px",
        }}
      >
        <h2
          style={{
            fontSize: "16px",
            fontWeight: "700",
            color: "var(--text-primary)",
            margin: 0,
          }}
        >
          Sessions{" "}
          <span
            style={{
              color: "var(--text-muted)",
              fontWeight: "500",
              fontSize: "13px",
            }}
          >
            ({total} total)
          </span>
        </h2>
        <div style={{ display: "flex", gap: "6px" }}>
          {["all", "active", "ended"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              style={{
                fontSize: "12px",
                padding: "5px 12px",
                borderRadius: "20px",
                border:
                  filter === f
                    ? "1px solid rgba(6,182,212,0.5)"
                    : "1px solid var(--border)",
                background:
                  filter === f ? "rgba(6,182,212,0.1)" : "transparent",
                color:
                  filter === f ? "var(--teal-bright)" : "var(--text-muted)",
                cursor: "pointer",
                fontWeight: filter === f ? "700" : "500",
                textTransform: "capitalize",
              }}
            >
              {f}
            </button>
          ))}
          <button
            onClick={load}
            style={{
              fontSize: "12px",
              padding: "5px 12px",
              borderRadius: "8px",
              border: "1px solid var(--border)",
              background: "transparent",
              color: "var(--text-muted)",
              cursor: "pointer",
            }}
          >
            ↺ Refresh
          </button>
        </div>
      </div>

      {error && (
        <div
          style={{
            padding: "10px 14px",
            background: "rgba(239,68,68,0.1)",
            border: "1px solid rgba(239,68,68,0.25)",
            borderRadius: "8px",
            color: "#f87171",
            fontSize: "13px",
            marginBottom: "16px",
          }}
        >
          {error}
          <button
            onClick={() => setError("")}
            style={{
              float: "right",
              background: "none",
              border: "none",
              color: "#f87171",
              cursor: "pointer",
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Revoke confirmation modal */}
      {revokeConfirm && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.7)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
        >
          <div
            style={{
              background: "var(--surface)",
              border: "1px solid var(--border)",
              borderRadius: "16px",
              padding: "28px",
              width: "100%",
              maxWidth: "380px",
            }}
          >
            <h3
              style={{
                margin: "0 0 10px",
                fontSize: "15px",
                fontWeight: "700",
              }}
            >
              Revoke Session?
            </h3>
            <p
              style={{
                fontSize: "13px",
                color: "var(--text-muted)",
                margin: "0 0 6px",
              }}
            >
              This will immediately end the active session for{" "}
              <strong style={{ color: "var(--text-primary)" }}>
                {userName(revokeConfirm)}
              </strong>
              .
            </p>
            <p
              style={{
                fontSize: "12px",
                color: "var(--text-muted)",
                margin: "0 0 20px",
              }}
            >
              Started {timeAgo(revokeConfirm.createdAt)}
              {revokeConfirm.ip && ` · IP: ${revokeConfirm.ip}`}
            </p>
            <div
              style={{
                display: "flex",
                gap: "10px",
                justifyContent: "flex-end",
              }}
            >
              <button
                className="btn"
                onClick={() => setRevokeConfirm(null)}
                style={{
                  background: "transparent",
                  border: "1px solid var(--border)",
                  color: "var(--text-muted)",
                }}
              >
                Cancel
              </button>
              <button
                className="btn"
                onClick={() => handleRevoke(revokeConfirm)}
                style={{
                  background: "rgba(239,68,68,0.15)",
                  border: "1px solid rgba(239,68,68,0.3)",
                  color: "#f87171",
                }}
              >
                Revoke Session
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div
          style={{
            textAlign: "center",
            padding: "40px",
            color: "var(--text-muted)",
          }}
        >
          Loading...
        </div>
      ) : filtered.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "40px",
            color: "var(--text-muted)",
            fontSize: "13px",
          }}
        >
          No sessions found
        </div>
      ) : (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            {filtered.map((session) => (
              <div
                key={session._id}
                style={{
                  background: "var(--surface)",
                  border: "1px solid var(--border)",
                  borderRadius: "10px",
                  padding: "12px 16px",
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  flexWrap: "wrap",
                }}
              >
                <div
                  style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    background: session.active
                      ? "#4ade80"
                      : "var(--text-muted)",
                    flexShrink: 0,
                  }}
                />
                <div style={{ flex: 1, minWidth: "150px" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      flexWrap: "wrap",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "var(--text-primary)",
                      }}
                    >
                      {userName(session)}
                    </span>
                    <span
                      style={{
                        fontSize: "10px",
                        padding: "2px 7px",
                        borderRadius: "5px",
                        background: session.active
                          ? "rgba(34,197,94,0.1)"
                          : "rgba(255,255,255,0.05)",
                        border: `1px solid ${session.active ? "rgba(34,197,94,0.3)" : "var(--border)"}`,
                        color: session.active ? "#4ade80" : "var(--text-muted)",
                        fontWeight: "700",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      {session.active ? "Active" : "Ended"}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: "11px",
                      color: "var(--text-muted)",
                      marginTop: "3px",
                      display: "flex",
                      gap: "10px",
                      flexWrap: "wrap",
                    }}
                  >
                    <span>Started {timeAgo(session.createdAt)}</span>
                    {session.ip && <span>IP: {session.ip}</span>}
                    {!session.active && session.endedAt && (
                      <span>Ended {timeAgo(session.endedAt)}</span>
                    )}
                    {session.expiresAt && session.active && (
                      <span>Expires {formatDatetime(session.expiresAt)}</span>
                    )}
                  </div>
                  {session.userAgent && (
                    <div
                      style={{
                        fontSize: "11px",
                        color: "var(--text-muted)",
                        marginTop: "1px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {session.userAgent}
                    </div>
                  )}
                </div>
                {isAdmin && session.active && (
                  <button
                    className="btn btn-small"
                    onClick={() => setRevokeConfirm(session)}
                    style={{
                      fontSize: "12px",
                      padding: "5px 12px",
                      background: "rgba(239,68,68,0.08)",
                      border: "1px solid rgba(239,68,68,0.25)",
                      color: "#f87171",
                      flexShrink: 0,
                    }}
                  >
                    Revoke
                  </button>
                )}
              </div>
            ))}
          </div>
          {total > LIMIT && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "10px",
                marginTop: "16px",
              }}
            >
              <button
                className="btn btn-small"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                style={{
                  fontSize: "12px",
                  padding: "5px 14px",
                  opacity: page === 1 ? 0.4 : 1,
                }}
              >
                ← Prev
              </button>
              <span
                style={{
                  fontSize: "12px",
                  color: "var(--text-muted)",
                  padding: "5px 10px",
                }}
              >
                Page {page} of {Math.ceil(total / LIMIT)}
              </span>
              <button
                className="btn btn-small"
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= Math.ceil(total / LIMIT)}
                style={{
                  fontSize: "12px",
                  padding: "5px 14px",
                  opacity: page >= Math.ceil(total / LIMIT) ? 0.4 : 1,
                }}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
